import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams, useLocation } from 'react-router-dom';
import { AuthProvider } from '@/shared/contexts/AuthContext';
import ProtectedRoute from '@/shared/components/ProtectedRoute';
import TenantStatusGuard from '@/shared/components/TenantStatusGuard';
import { EmpresaIdGuard } from '@/shared/components/EmpresaIdGuard';
import { ModuloGate } from '@/shared/components/ModuloGate';
import { Login, ContaSuspensaPage } from '@/features/auth';
import { Player, BloqueadoPage } from '@/features/player';
import { JukeboxLayout, JukeboxHomePage, JukeboxHistoricoPage, JukeboxPerfilPage } from '@/features/jukebox';
import TermometroPage from '@/features/futebol/presentation/pages/TermometroPage';
import JogoAoVivoPage from '@/features/futebol/presentation/pages/JogoAoVivoPage';
import RootRoute                        from '@/features/landing/presentation/pages/RootRoute';
import { PwaEntryTracker }              from '@/shared/components/PwaEntryTracker';

// Rotas de admin/tenant: lazy-loaded para não pesar o bundle inicial do Player/Jukebox.
const TenantHome           = lazy(() => import('@/features/dashboard/presentation/pages/TenantHome'));
const Dashboard            = lazy(() => import('@/features/dashboard/presentation/pages/Dashboard'));
const AdminMaster          = lazy(() => import('@/features/admin/presentation/pages/AdminMaster'));
const ControleRemoto       = lazy(() => import('@/features/controle-remoto/presentation/pages/ControleRemoto'));
const ModeracaoSugestoes   = lazy(() => import('@/features/controle-remoto/presentation/pages/ModeracaoSugestoes'));
const ConfiguracoesUsuario = lazy(() => import('@/features/configuracoes/presentation/pages/ConfiguracoesUsuario'));
const WhatsAppPage         = lazy(() => import('@/features/configuracoes/presentation/pages/WhatsAppPage'));
const DashboardMetricas    = lazy(() => import('@/features/metricas/presentation/pages/DashboardMetricas'));
const FutebolAdmin         = lazy(() => import('@/features/futebol/presentation/pages/FutebolAdmin'));
const AnunciantesPage      = lazy(() => import('@/features/anunciantes/presentation/pages/AnunciantesPage'));
const MomentoEspecialPage  = lazy(() => import('@/features/momento-especial/presentation/pages/MomentoEspecialPage').then(m => ({ default: m.MomentoEspecialPage })));
const IdentidadeSonoraPage = lazy(() => import('@/features/identidade-sonora/presentation/pages/IdentidadeSonoraPage').then(m => ({ default: m.IdentidadeSonoraPage })));
const PdvEngajamentoPage   = lazy(() => import('@/features/pdv-engajamento').then(m => ({ default: m.PdvEngajamentoPage })));
const RelatorioCampanhaPage = lazy(() => import('@/features/relatorio-campanha').then(m => ({ default: m.RelatorioCampanhaPage })));

// BrowserRouter não reseta o scroll da janela ao trocar de rota — sem isso, tiles no fim
// do TenantHome (Métricas, Configurações) abriam a página já rolada no mobile.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function JogoLegacyRedirect() {
  const { telaId } = useParams<{ telaId: string }>();
  return <Navigate to={`/jukebox/${telaId ?? ''}/jogo`} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PwaEntryTracker />
        <ScrollToTop />
        <Routes>
          {/* ── Pública: Login ──────────────────────────────── */}
          <Route path="/login" element={<Login />} />

          {/* ── Pública: Player da TV e Jukebox mobile ─────── */}
          <Route path="/player/:telaId"  element={<Player />} />
          <Route path="/bloqueado"       element={<BloqueadoPage />} />
          <Route path="/jukebox/:telaId" element={<JukeboxLayout />}>
            <Route index element={<JukeboxHomePage />} />
            <Route path="historico" element={<JukeboxHistoricoPage />} />
            <Route path="jogo" element={<JogoAoVivoPage />} />
            <Route path="perfil" element={<JukeboxPerfilPage />} />
          </Route>
          <Route path="/termometro/:telaId" element={<TermometroPage />} />
          <Route path="/jogo/:telaId" element={<JogoLegacyRedirect />} />
          <Route path="/relatorio-campanha/:token" element={<Suspense fallback={null}><RelatorioCampanhaPage /></Suspense>} />

          {/* ── Protegida: Painel Master (super_admin) ──────── */}
          <Route element={<ProtectedRoute requiredRole="super_admin" />}>
            <Route path="/admin-master" element={<Suspense fallback={null}><AdminMaster /></Suspense>} />
          </Route>

          {/* ── Protegida: Painel do Tenant ─────────────────── */}
          <Route element={<ProtectedRoute requiredRole="tenant" />}>
            <Route path="/conta-suspensa" element={<ContaSuspensaPage />} />
            <Route element={<TenantStatusGuard />}>
              <Route element={<EmpresaIdGuard />}>
              <Route element={<Suspense fallback={null}><Outlet /></Suspense>}>
                <Route path="/dashboard"             element={<TenantHome />} />
                <Route path="/dashboard/:empresaId"  element={<TenantHome />} />
                <Route path="/telas"                           element={<Dashboard />} />
                <Route path="/telas/:empresaId"                element={<Dashboard />} />
                <Route path="/controle-remoto"                 element={<ControleRemoto />} />
                <Route path="/controle-remoto/:empresaId"      element={<ControleRemoto />} />
                <Route path="/moderacao-sugestoes"             element={<ModeracaoSugestoes />} />
                <Route path="/moderacao-sugestoes/:empresaId"  element={<ModeracaoSugestoes />} />
                <Route path="/configuracoes"                   element={<ConfiguracoesUsuario />} />
                <Route path="/configuracoes/:empresaId"        element={<ConfiguracoesUsuario />} />
                <Route path="/metricas"                        element={<DashboardMetricas />} />
                <Route path="/metricas/:empresaId"             element={<DashboardMetricas />} />
                <Route path="/futebol"                         element={<ModuloGate modulo="futebol"><FutebolAdmin /></ModuloGate>} />
                <Route path="/futebol/:empresaId"              element={<ModuloGate modulo="futebol"><FutebolAdmin /></ModuloGate>} />
                <Route path="/whatsapp"                        element={<ModuloGate modulo="whatsapp"><WhatsAppPage /></ModuloGate>} />
                <Route path="/whatsapp/:empresaId"             element={<ModuloGate modulo="whatsapp"><WhatsAppPage /></ModuloGate>} />
                <Route path="/anunciantes"                     element={<ModuloGate modulo="anunciantes"><AnunciantesPage /></ModuloGate>} />
                <Route path="/anunciantes/:empresaId"          element={<ModuloGate modulo="anunciantes"><AnunciantesPage /></ModuloGate>} />
                <Route path="/momento-especial"                element={<MomentoEspecialPage />} />
                <Route path="/momento-especial/:empresaId"      element={<MomentoEspecialPage />} />
                <Route path="/identidade-sonora"                element={<ModuloGate modulo="identidade_sonora"><IdentidadeSonoraPage /></ModuloGate>} />
                <Route path="/identidade-sonora/:empresaId"     element={<ModuloGate modulo="identidade_sonora"><IdentidadeSonoraPage /></ModuloGate>} />
                <Route path="/pdv-engajamento"                  element={<ModuloGate modulo="pdv"><PdvEngajamentoPage /></ModuloGate>} />
                <Route path="/pdv-engajamento/:empresaId"       element={<ModuloGate modulo="pdv"><PdvEngajamentoPage /></ModuloGate>} />
              </Route>
              </Route>
            </Route>
          </Route>

          {/* ── Raiz: landing (web) ou redirect PWA/sessão ─ */}
          <Route path="/" element={<RootRoute />} />

          {/* ── Fallback ────────────────────────────────────── */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
